import { useEffect, useMemo, useState } from 'react';
import { useCalendarLocale } from './hooks/useCalendarLocale';
import { useCalendarStyles } from './Calendar.styles';
import CalendarDayGrid from './components/CalendarDayGrid';
import CalendarDayHeaders from './components/CalendarDayHeaders';
import CalendarHeader from './components/CalendarHeader';
import { buildCalendarDays } from './operators/buildCalendarDays';
import { getAvailableMonthKeys, monthKey } from './operators/getAvailableMonthKeys';
import { getStartMonth } from './operators/getStartMonth';
import { resolveTimeZone, toDayStrings } from '../../util/datetime';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Calendar = ({
  name,
  selectedDate,
  availableDates,
  timezone,
  isDisabled = false,
  onChange
}) => {
  const zone = useMemo(() => resolveTimeZone(timezone), [timezone]);
  const availableDays = useMemo(() => toDayStrings(availableDates, zone), [availableDates, zone]);
  const availableDateSet = useMemo(() => new Set(availableDays), [availableDays]);
  useEffect(() => {
    if (selectedDate && !availableDateSet.has(selectedDate)) {
      onChange(name, '');
    }
  }, [selectedDate, availableDateSet, name, onChange]);
  const availableMonthKeys = useMemo(() => getAvailableMonthKeys(availableDays), [availableDays]);
  const [current, setCurrent] = useState(() => getStartMonth(availableDays));
  useEffect(() => {
    if (!availableMonthKeys.has(monthKey(current))) {
      const newStart = getStartMonth(availableDays);
      if (monthKey(newStart) !== monthKey(current)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCurrent(newStart);
      }
    }
  }, [availableMonthKeys, current, availableDays]);
  const prevMonth = current.month === 0 ? {
    year: current.year - 1,
    month: 11
  } : {
    year: current.year,
    month: current.month - 1
  };
  const nextMonth = current.month === 11 ? {
    year: current.year + 1,
    month: 0
  } : {
    year: current.year,
    month: current.month + 1
  };
  const canGoPrev = availableMonthKeys.has(monthKey(prevMonth));
  const canGoNext = availableMonthKeys.has(monthKey(nextMonth));
  const {
    firstDayOfWeek,
    monthLabel
  } = useCalendarLocale(current);
  const calendarDays = useMemo(() => buildCalendarDays(current.year, current.month, firstDayOfWeek), [current, firstDayOfWeek]);
  const {
    disabledContainerStyle
  } = useCalendarStyles();
  return /*#__PURE__*/_jsxs("div", {
    role: "group",
    "aria-label": monthLabel,
    style: isDisabled ? disabledContainerStyle : undefined,
    children: [/*#__PURE__*/_jsx(CalendarHeader, {
      monthLabel: monthLabel,
      canGoPrev: canGoPrev,
      canGoNext: canGoNext,
      isDisabled: isDisabled,
      onPrev: () => setCurrent(prevMonth),
      onNext: () => setCurrent(nextMonth)
    }), /*#__PURE__*/_jsx(CalendarDayHeaders, {
      firstDayOfWeek: firstDayOfWeek
    }), /*#__PURE__*/_jsx(CalendarDayGrid, {
      calendarDays: calendarDays,
      availableDateSet: availableDateSet,
      selectedDate: selectedDate,
      isDisabled: isDisabled,
      onDayClick: dateStr => onChange(name, dateStr)
    })]
  });
};
export default Calendar;